import { useQuery, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  getAutomationScripts,
  getAutomationScriptMetadata,
  getJobsList,
  getJobStatus,
} from "api/Repos/automation"
import {
  ScriptMetadata,
  JobListResponse,
  JobStatusResponse,
  AvailableScriptsResponse,
} from "types/automation.types"
import { isJobCompleted } from "utils/automationUtils"

/**
 * React-query queries for the Automation domain (mutations live in
 * `useAutomationMutations.ts`). The job queries replace the hand-rolled
 * setInterval machinery that used to live in `useJobPolling` — polling is
 * expressed as `refetchInterval` and stops by returning `false`.
 */

/** Available automation script names (raw response; callers filter). */
export const useAutomationScriptsQuery = (
  enabled: boolean = true
): UseQueryResult<AvailableScriptsResponse> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.automation.scripts,
    enabled,
    meta: { errorTitle: "Error Loading Automation Scripts!" },
    queryFn: async (): Promise<AvailableScriptsResponse> =>
      (await getAutomationScripts(await getAxios())).data,
  })
}

/** Metadata (parameters etc.) for one script; rarely changes → 5-min cache. */
export const useScriptMetadataQuery = (
  scriptName: string,
  enabled: boolean = true
): UseQueryResult<ScriptMetadata> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.automation.scriptMetadata(scriptName),
    enabled: enabled && Boolean(scriptName),
    staleTime: 5 * 60_000,
    meta: { errorTitle: "Failed to load script metadata" },
    queryFn: async (): Promise<ScriptMetadata> =>
      (await getAutomationScriptMetadata(await getAxios(), scriptName)).data,
  })
}

/** Jobs list, polled every 2s while enabled. Errors are silent (poll spam). */
export const useJobsQuery = (
  enabled: boolean
): UseQueryResult<JobListResponse[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.automation.jobs,
    enabled,
    refetchInterval: 2_000,
    meta: { silent: true },
    queryFn: async (): Promise<JobListResponse[]> => {
      const res = await getJobsList(await getAxios())
      return (res.data as JobListResponse[]) ?? []
    },
  })
}

/**
 * Status of one job, polled every second until the job reports a completed
 * state (then the interval stops but the final status stays available).
 */
export const useJobStatusQuery = (
  jobId: string | null
): UseQueryResult<JobStatusResponse> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.automation.jobStatus(jobId ?? ""),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      query.state.data && isJobCompleted(query.state.data.status)
        ? false
        : 1_000,
    meta: { silent: true },
    queryFn: async (): Promise<JobStatusResponse> =>
      (await getJobStatus(await getAxios(), jobId as string)).data,
  })
}
